<template>
  <div class="st-page">
    <Navigation />
    <div class="container" style="padding-top:20px;">
      <div class="d-flex align-items-center justify-content-between mb-4">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
          <div><h4 class="st-page-title"><i class="mdi mdi-cog me-2"></i>Settings</h4></div>
        </div>
      </div>

      <div class="st-settings-layout">
        <!-- Sidebar Nav -->
        <div class="st-settings-nav">
          <div class="st-nav-item" :class="{active: activeSection === s.id}" v-for="s in sections" :key="s.id" @click="activeSection = s.id">
            <i class="mdi" :class="s.icon"></i>
            <span>{{ s.label }}</span>
          </div>
        </div>

        <!-- Content -->
        <div class="st-settings-content">
          <div v-if="loading" class="st-spinner"><div class="spinner-border"></div></div>

          <!-- INSTITUTION PROFILE -->
          <div v-if="activeSection === 'profile'" class="st-section">
            <div class="st-section-title"><i class="mdi mdi-domain me-2"></i>Institution Profile</div>
            <div class="st-section-desc">Basic information about your school or institution.</div>
            <div class="st-alert st-alert-success" v-if="saveMsg === 'profile'">Settings saved!</div>

            <!-- Logo Upload -->
            <div class="mb-3" style="display:flex;align-items:center;gap:16px;flex-wrap:wrap;">
              <div class="logo-preview" @click="$refs.logoInput.click()">
                <img v-if="profile.logo" :src="profile.logo" class="logo-img" />
                <div v-else class="logo-placeholder"><i class="mdi mdi-camera-plus" style="font-size:24px;"></i><span style="font-size:10px;">Upload Logo</span></div>
              </div>
              <div style="flex:1;">
                <label class="st-label">Institution Logo</label>
                <div style="font-size:11px;color:#4A6A8A;margin-bottom:6px;">Recommended: Square image, min 200×200px. Used on ID cards, reports, parent portal.</div>
                <div class="d-flex gap-2">
                  <button class="btn st-btn-outline btn-sm" style="font-size:11px;padding:4px 12px;" @click="$refs.logoInput.click()">📷 {{ profile.logo ? 'Change' : 'Upload' }}</button>
                  <button v-if="profile.logo" class="btn st-btn-outline btn-sm" style="font-size:11px;padding:4px 12px;color:#EF4444;border-color:rgba(239,68,68,.3);" @click="removeLogo">🗑 Remove</button>
                </div>
                <input type="file" ref="logoInput" accept="image/*" @change="uploadLogo" style="display:none;" />
                <div v-if="logoUploading" style="font-size:11px;color:#4FC3F7;margin-top:4px;"><span class="spinner-border spinner-border-sm me-1"></span>Uploading...</div>
              </div>
            </div>

            <div class="row g-3 mb-3">
              <div class="col-md-8"><label class="st-label">Institution Name *</label><input class="st-input" v-model="profile.name" /></div>
              <div class="col-md-4">
                <label class="st-label">Type</label>
                <select class="st-select" v-model="profile.type">
                  <option value="primary">Primary School</option>
                  <option value="secondary">Secondary School</option>
                  <option value="mixed">Mixed (Primary + Secondary)</option>
                  <option value="boarding">Boarding School</option>
                  <option value="international">International School</option>
                  <option value="university">University/College</option>
                </select>
              </div>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-md-6"><label class="st-label">Email</label><input class="st-input" v-model="profile.email" /></div>
              <div class="col-md-6"><label class="st-label">Phone</label><input class="st-input" v-model="profile.phone" /></div>
            </div>
            <div class="mb-3"><label class="st-label">Address</label><input class="st-input" v-model="profile.address" placeholder="P.O. Box, Town, County" /></div>
            <div class="row g-3 mb-3">
              <div class="col-md-4"><label class="st-label">County</label><input class="st-input" v-model="profile.county" /></div>
              <div class="col-md-4"><label class="st-label">Country</label><input class="st-input" v-model="profile.country" /></div>
              <div class="col-md-4"><label class="st-label">Website</label><input class="st-input" v-model="profile.website" placeholder="https://" /></div>
            </div>
            <div class="mb-3"><label class="st-label">Motto / Tagline</label><input class="st-input" v-model="profile.motto" /></div>

            <!-- Principal Signature & Official Stamp -->
            <div style="border-top:1px solid #1A3A6A;margin:16px 0;padding-top:16px;">
              <div style="font-size:13px;font-weight:600;color:#4FC3F7;margin-bottom:10px;">🖊 ID Card Signatures</div>
              <div style="font-size:11px;color:#4A6A8A;margin-bottom:12px;">Upload the principal's signature and official stamp. These will appear on printed student ID cards.</div>
              <div class="d-flex gap-4 flex-wrap">
                <!-- Principal Signature -->
                <div style="text-align:center;">
                  <div class="sig-preview" @click="$refs.sigInput.click()">
                    <img v-if="profile.principalSignature" :src="profile.principalSignature" class="sig-img" />
                    <div v-else class="sig-placeholder"><i class="mdi mdi-draw" style="font-size:20px;"></i><span>Signature</span></div>
                  </div>
                  <div style="font-size:11px;color:#8BA4C4;margin:4px 0;">Principal's Signature</div>
                  <div class="d-flex gap-1 justify-content-center">
                    <button class="btn st-btn-outline btn-sm" style="font-size:10px;padding:2px 10px;" @click="$refs.sigInput.click()">📷 {{ profile.principalSignature ? 'Change' : 'Upload' }}</button>
                    <button v-if="profile.principalSignature" class="btn st-btn-outline btn-sm" style="font-size:10px;padding:2px 10px;color:#EF4444;border-color:rgba(239,68,68,.3);" @click="removeFile('principalSignature')">✕</button>
                  </div>
                  <input type="file" ref="sigInput" accept="image/*" @change="uploadFile($event, 'principalSignature', 'principal_signature')" style="display:none;" />
                </div>
                <!-- Official Stamp -->
                <div style="text-align:center;">
                  <div class="sig-preview" @click="$refs.stampInput.click()">
                    <img v-if="profile.officialStamp" :src="profile.officialStamp" class="sig-img" />
                    <div v-else class="sig-placeholder"><i class="mdi mdi-stamper" style="font-size:20px;"></i><span>Stamp</span></div>
                  </div>
                  <div style="font-size:11px;color:#8BA4C4;margin:4px 0;">Official Stamp</div>
                  <div class="d-flex gap-1 justify-content-center">
                    <button class="btn st-btn-outline btn-sm" style="font-size:10px;padding:2px 10px;" @click="$refs.stampInput.click()">📷 {{ profile.officialStamp ? 'Change' : 'Upload' }}</button>
                    <button v-if="profile.officialStamp" class="btn st-btn-outline btn-sm" style="font-size:10px;padding:2px 10px;color:#EF4444;border-color:rgba(239,68,68,.3);" @click="removeFile('officialStamp')">✕</button>
                  </div>
                  <input type="file" ref="stampInput" accept="image/*" @change="uploadFile($event, 'officialStamp', 'official_stamp')" style="display:none;" />
                </div>
              </div>
              <div v-if="fileUploading" style="font-size:11px;color:#4FC3F7;margin-top:6px;"><span class="spinner-border spinner-border-sm me-1"></span>Uploading...</div>
            </div>

            <button class="btn st-btn-primary mt-3" @click="saveProfile" :disabled="saving"><span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>Save Profile</button>
          </div>


          <!-- M-PESA CONFIGURATION -->
          <div v-if="activeSection === 'mpesa'" class="st-section">
            <div class="st-section-title"><i class="mdi mdi-cellphone me-2"></i>M-Pesa Paybills</div>
            <div class="st-section-desc">Manage your Safaricom Daraja paybills and till numbers.</div>

            <!-- Setup Guide -->
            <div style="margin-bottom:16px;">
              <button class="btn btn-sm" @click="showSetupGuide = !showSetupGuide"
                      style="background:rgba(79,195,247,.08);border:1px solid rgba(79,195,247,.2);color:#4FC3F7;border-radius:8px;padding:8px 16px;font-size:13px;font-weight:600;">
                {{ showSetupGuide ? '✕ Hide Guide' : '📖 How to get your Daraja credentials' }}
              </button>

              <div v-if="showSetupGuide" style="background:#0A1628;border:1px solid #1A3A6A;border-radius:12px;padding:20px;margin-top:12px;line-height:1.8;">

                <!-- Step 1 -->
                <div style="margin-bottom:20px;">
                  <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
                    <span style="background:#2E86DE;color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;flex-shrink:0;">1</span>
                    <strong style="color:#E8EDF4;font-size:14px;">Create a Daraja Account & App</strong>
                  </div>
                  <div style="padding-left:36px;font-size:13px;color:#8BA4C4;">
                    Go to <a href="https://developer.safaricom.co.ke" target="_blank" style="color:#4FC3F7;font-weight:600;">developer.safaricom.co.ke</a> and sign up or log in.
                    Click <strong style="color:#E8EDF4;">"My Apps"</strong> → <strong style="color:#E8EDF4;">"Add a New App"</strong>.
                    Give it a name (e.g. "Simi Trac") and select the APIs you need: <strong style="color:#E8EDF4;">Lipa Na M-Pesa</strong>, <strong style="color:#E8EDF4;">C2B</strong>, <strong style="color:#E8EDF4;">Transaction Status</strong>, <strong style="color:#E8EDF4;">Reversal</strong>, <strong style="color:#E8EDF4;">Account Balance</strong>.
                  </div>
                </div>

                <!-- Step 2 -->
                <div style="margin-bottom:20px;">
                  <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
                    <span style="background:#2E86DE;color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;flex-shrink:0;">2</span>
                    <strong style="color:#E8EDF4;font-size:14px;">Get Consumer Key & Consumer Secret</strong>
                  </div>
                  <div style="padding-left:36px;font-size:13px;color:#8BA4C4;">
                    After creating the app, click on it. You'll see:
                    <div style="background:#0F2847;border-radius:8px;padding:10px 14px;margin:8px 0;font-family:monospace;font-size:12px;">
                      <div>Consumer Key: <span style="color:#10B981;">4bx4A7KTU5ect...</span></div>
                      <div>Consumer Secret: <span style="color:#10B981;">KClbyYqiWI...</span></div>
                    </div>
                    Copy both and paste them in the form below.
                  </div>
                </div>

                <!-- Step 3 -->
                <div style="margin-bottom:20px;">
                  <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
                    <span style="background:#2E86DE;color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;flex-shrink:0;">3</span>
                    <strong style="color:#E8EDF4;font-size:14px;">Get Shortcode & Passkey</strong>
                  </div>
                  <div style="padding-left:36px;font-size:13px;color:#8BA4C4;">
                    <strong style="color:#E8EDF4;">Shortcode</strong> is your Paybill or Till number (e.g. <span style="color:#4FC3F7;">4160303</span>). You get this from Safaricom when you register your business for M-Pesa.
                    <br/><strong style="color:#E8EDF4;">Passkey</strong> (Lipa Na M-Pesa Online Passkey) is provided by Safaricom when you request "Go Live" for your app. Contact Safaricom M-Pesa support or your Daraja portal manager to get it.
                    <div style="background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.2);border-radius:8px;padding:8px 12px;margin-top:8px;color:#F59E0B;font-size:12px;">
                      ⚠️ The Passkey is NOT the same as the Consumer Secret. It's a separate key specifically for STK Push (Lipa Na M-Pesa).
                    </div>
                  </div>
                </div>

                <!-- Step 4 -->
                <div style="margin-bottom:20px;">
                  <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
                    <span style="background:#2E86DE;color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;flex-shrink:0;">4</span>
                    <strong style="color:#E8EDF4;font-size:14px;">Get Initiator Name</strong>
                  </div>
                  <div style="padding-left:36px;font-size:13px;color:#8BA4C4;">
                    In the Daraja portal, go to <strong style="color:#E8EDF4;">"APIs"</strong> → look for <strong style="color:#E8EDF4;">"API Operators"</strong> or check your M-Pesa Web Portal.
                    The Initiator Name is the API operator username assigned to your organization (e.g. <span style="color:#4FC3F7;">testapi</span> for sandbox, or your organization's operator name for production).
                    <br/>This is needed for checking transaction status, account balance, and reversals.
                  </div>
                </div>

                <!-- Step 5 -->
                <div style="margin-bottom:12px;">
                  <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
                    <span style="background:#10B981;color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;flex-shrink:0;">5</span>
                    <strong style="color:#E8EDF4;font-size:14px;">Generate Security Credential</strong>
                  </div>
                  <div style="padding-left:36px;font-size:13px;color:#8BA4C4;">
                    This encrypts your Initiator Password so it can be sent securely to Safaricom:
                    <ol style="margin:8px 0;padding-left:20px;color:#8BA4C4;">
                      <li>Go to <a href="https://developer.safaricom.co.ke" target="_blank" style="color:#4FC3F7;">developer.safaricom.co.ke</a></li>
                      <li>Click <strong style="color:#E8EDF4;">"APIs"</strong> in the top menu</li>
                      <li>Scroll down and find <strong style="color:#E8EDF4;">"Security Credentials"</strong></li>
                      <li>In the <strong style="color:#E8EDF4;">"Initiator Security Credential"</strong> section:
                        <div style="background:#0F2847;border-radius:8px;padding:10px 14px;margin:6px 0;">
                          Select: <strong style="color:#4FC3F7;">Production</strong> (or Sandbox for testing)<br/>
                          Enter: Your <strong style="color:#4FC3F7;">Initiator Password</strong><br/>
                          Click: <strong style="color:#10B981;">"Generate"</strong>
                        </div>
                      </li>
                      <li>Copy the long encrypted text that appears</li>
                      <li>Paste it in the <strong style="color:#E8EDF4;">"Security Credential"</strong> field below</li>
                    </ol>
                    <div style="background:rgba(16,185,129,.08);border:1px solid rgba(16,185,129,.2);border-radius:8px;padding:8px 12px;color:#10B981;font-size:12px;">
                      ✅ Once you have the Security Credential, you can verify transactions, check balances, and reverse payments directly from Simi Trac.
                    </div>
                  </div>
                </div>

                <!-- Summary -->
                <div style="background:#0F2847;border-radius:8px;padding:14px;margin-top:16px;">
                  <div style="font-size:13px;font-weight:600;color:#4FC3F7;margin-bottom:8px;">📋 Summary — What you need</div>
                  <table style="width:100%;font-size:12px;color:#8BA4C4;border-collapse:collapse;">
                    <tr style="border-bottom:1px solid #1A3A6A;"><td style="padding:6px 0;color:#E8EDF4;font-weight:600;">Consumer Key</td><td style="padding:6px 0;">Daraja Portal → Your App</td><td style="padding:6px 0;color:#10B981;">Required</td></tr>
                    <tr style="border-bottom:1px solid #1A3A6A;"><td style="padding:6px 0;color:#E8EDF4;font-weight:600;">Consumer Secret</td><td style="padding:6px 0;">Daraja Portal → Your App</td><td style="padding:6px 0;color:#10B981;">Required</td></tr>
                    <tr style="border-bottom:1px solid #1A3A6A;"><td style="padding:6px 0;color:#E8EDF4;font-weight:600;">Shortcode</td><td style="padding:6px 0;">Your Paybill / Till number from Safaricom</td><td style="padding:6px 0;color:#10B981;">Required</td></tr>
                    <tr style="border-bottom:1px solid #1A3A6A;"><td style="padding:6px 0;color:#E8EDF4;font-weight:600;">Passkey</td><td style="padding:6px 0;">From Safaricom (Lipa Na M-Pesa Online)</td><td style="padding:6px 0;color:#10B981;">Required for STK</td></tr>
                    <tr style="border-bottom:1px solid #1A3A6A;"><td style="padding:6px 0;color:#E8EDF4;font-weight:600;">Initiator Name</td><td style="padding:6px 0;">API operator username from M-Pesa portal</td><td style="padding:6px 0;color:#F59E0B;">For advanced APIs</td></tr>
                    <tr><td style="padding:6px 0;color:#E8EDF4;font-weight:600;">Security Credential</td><td style="padding:6px 0;">Generated on Daraja → APIs → Security Credentials</td><td style="padding:6px 0;color:#F59E0B;">For advanced APIs</td></tr>
                  </table>
                </div>

              </div>
            </div>

            <!-- Add Paybill Button -->
            <button class="btn st-btn-primary btn-sm mb-3" @click="showAddPaybill = !showAddPaybill">{{ showAddPaybill ? '✕ Cancel' : '+ Add Paybill' }}</button>

            <!-- Add / Edit Form -->
            <div v-if="showAddPaybill || editingPaybill" style="background:#0A1628;border-radius:10px;padding:16px;margin-bottom:16px;border-left:3px solid #4FC3F7;">
              <h6 style="color:#4FC3F7;margin:0 0 12px;font-size:14px;">{{ editingPaybill ? '✏️ Edit Paybill' : '➕ New Paybill' }}</h6>
              <div class="row g-3 mb-3">
                <div class="col-md-3"><label class="st-label">Shortcode</label><input class="st-input" v-model="pbForm.shortcode" placeholder="e.g. 4160303" /></div>
                <div class="col-md-3"><label class="st-label">Account Prefix</label><input class="st-input" v-model="pbForm.accountPrefix" placeholder="e.g. SPI" style="text-transform:uppercase;" /><span style="font-size:10px;color:#4A6A8A;">Required if paybill is shared with other schools</span></div>
                <div class="col-md-3"><label class="st-label">Label</label><input class="st-input" v-model="pbForm.label" placeholder="e.g. School Fees" /></div>
                <div class="col-md-3">
                  <label class="st-label">Purpose</label>
                  <select class="st-select" v-model="pbForm.purpose">
                    <option value="all">All Payments</option>
                    <option value="wallet_topup">Wallet Top-ups</option>
                    <option value="fee_payment">School Fees</option>
                    <option value="pos_payment">Canteen / POS</option>
                    <option value="sms_purchase">SMS Purchases</option>
                    <option value="donation">Donations</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="st-label">Type</label>
                  <select class="st-select" v-model="pbForm.type">
                    <option value="paybill">Paybill</option>
                    <option value="till">Till Number (Buy Goods)</option>
                  </select>
                </div>
                <div class="col-md-3" v-if="pbForm.type === 'till'">
                  <label class="st-label">Till Number (PartyB)</label>
                  <input class="st-input" v-model="pbForm.tillNumber" placeholder="e.g. 5076432" />
                  <span style="font-size:10px;color:#4A6A8A;">Where customers actually pay (PartyB) — different from Go-Live shortcode</span>
                </div>
              </div>
              <div class="row g-3 mb-3">
                <div class="col-md-6"><label class="st-label">Consumer Key <span style="font-size:10px;color:#4A6A8A;">— Step 2 in guide</span></label><input class="st-input" v-model="pbForm.consumerKey" placeholder="From Daraja → Your App → Consumer Key" /></div>
                <div class="col-md-6"><label class="st-label">Consumer Secret <span style="font-size:10px;color:#4A6A8A;">— Step 2 in guide</span></label><input class="st-input" v-model="pbForm.consumerSecret" placeholder="From Daraja → Your App → Consumer Secret" /></div>
              </div>
              <div class="row g-3 mb-3">
                <div class="col-md-4"><label class="st-label">Passkey <span style="font-size:10px;color:#4A6A8A;">— Step 3 in guide</span></label><input class="st-input" v-model="pbForm.passkey" placeholder="Lipa Na M-Pesa Online passkey from Safaricom" /></div>
                <div class="col-md-4">
                  <label class="st-label">Environment</label>
                  <select class="st-select" v-model="pbForm.environment">
                    <option value="sandbox">Sandbox</option>
                    <option value="production">Production</option>
                  </select>
                </div>
                <div class="col-md-4" style="display:flex;align-items:flex-end;">
                  <label style="font-size:12px;color:#8BA4C4;cursor:pointer;display:flex;align-items:center;gap:6px;">
                    <input type="checkbox" v-model="pbForm.isPrimary" /> Set as Primary (for STK Push)
                  </label>
                </div>
              </div>
              <div class="row g-3 mb-3">
                <div class="col-md-12"><label class="st-label">Initiator Name <span style="font-size:10px;color:#4A6A8A;">— Step 4 in guide</span></label><input class="st-input" v-model="pbForm.initiatorName" placeholder="API operator username (e.g. testapi)" /></div>
              </div>
              <div class="mb-3">
                <label class="st-label">Security Credential <span style="font-size:10px;color:#10B981;">— Step 5 in guide (Recommended)</span></label>
                <textarea class="st-input" v-model="pbForm.securityCredential" rows="2" placeholder="Paste the encrypted credential from Daraja → APIs → Security Credentials → Generate" style="font-family:monospace;font-size:12px;"></textarea>
                <span style="font-size:10px;color:#4A6A8A;">Go to <a href="https://developer.safaricom.co.ke" target="_blank" style="color:#4FC3F7;">Daraja Portal</a> → APIs → Security Credentials → Select Production → Enter initiator password → Generate → Copy and paste here</span>
              </div>
              <div class="d-flex gap-2">
                <button class="btn st-btn-primary btn-sm" @click="savePaybill" :disabled="saving">
                  <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>{{ editingPaybill ? 'Update' : 'Add Paybill' }}
                </button>
                <button v-if="editingPaybill" class="btn btn-sm" style="color:#8BA4C4;" @click="editingPaybill=null; showAddPaybill=false;">Cancel</button>
              </div>
            </div>

            <!-- Paybill Cards -->
            <div v-if="paybills.length === 0 && !showAddPaybill" style="text-align:center;padding:40px;color:#4A6A8A;">
              <div style="font-size:48px;margin-bottom:12px;">💳</div>
              <p>No paybills configured yet. Click "Add Paybill" to get started.</p>
            </div>

            <div v-for="pb in paybills" :key="pb.id" style="background:#0A1628;border-radius:10px;padding:16px;margin-bottom:12px;border-left:3px solid;" :style="{borderLeftColor: pb.isPrimary ? '#10B981' : (pb.isActive ? '#4FC3F7' : '#4A6A8A')}">
              <!-- Header -->
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:6px;">
                <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
                  <span style="font-size:18px;font-weight:700;color:#E8EDF4;">{{ pb.shortcode }}</span>
                  <span style="font-size:12px;padding:2px 8px;border-radius:4px;font-weight:600;" :style="{background: pb.type==='till' ? 'rgba(245,158,11,.15)' : 'rgba(79,195,247,.15)', color: pb.type==='till' ? '#F59E0B' : '#4FC3F7'}">{{ pb.type.toUpperCase() }}</span>
                  <span v-if="pb.isPrimary" style="font-size:11px;padding:2px 8px;border-radius:4px;background:rgba(16,185,129,.15);color:#10B981;font-weight:600;">⭐ PRIMARY</span>
                  <span v-if="pb.callbacksRegistered" style="font-size:11px;padding:2px 8px;border-radius:4px;background:rgba(16,185,129,.1);color:#10B981;">✅ Active</span>
                  <span v-if="!pb.isActive" style="font-size:11px;padding:2px 8px;border-radius:4px;background:rgba(239,68,68,.1);color:#EF4444;">Disabled</span>
                </div>
                <div style="display:flex;gap:6px;">
                  <button class="btn btn-sm" style="font-size:11px;padding:2px 8px;color:#4FC3F7;border:1px solid rgba(79,195,247,.3);border-radius:4px;" @click="editPaybill(pb)">✏️</button>
                  <button class="btn btn-sm" style="font-size:11px;padding:2px 8px;color:#EF4444;border:1px solid rgba(239,68,68,.3);border-radius:4px;" @click="deletePaybill(pb)">🗑️</button>
                </div>
              </div>
              <!-- Details -->
              <div style="display:flex;gap:16px;flex-wrap:wrap;font-size:12px;color:#8BA4C4;margin-bottom:10px;">
                <span><strong>{{ pb.label }}</strong></span>
                <span v-if="pb.accountPrefix" style="color:#F59E0B;">Prefix: <strong>{{ pb.accountPrefix }}</strong></span>
                <span>Purpose: {{ pb.purpose }}</span>
                <span>Env: {{ pb.environment }}</span>
                <span>Key: {{ pb.consumerKey ? pb.consumerKey.substring(0,8) + '••••' : '❌' }}</span>
                <span>Initiator: <strong :style="{color: pb.initiatorName ? '#10B981' : '#4A6A8A'}">{{ pb.initiatorName ? '✅' : '—' }}</strong></span>
              </div>
              <!-- Actions -->
              <div class="d-flex gap-2 flex-wrap">
                <button class="btn btn-sm st-btn-outline-info" @click="testPaybillConnection(pb)" :disabled="pb._testing" style="font-size:11px;padding:4px 10px;">
                  <span v-if="pb._testing" class="spinner-border spinner-border-sm me-1"></span>{{ pb._testing ? '...' : '🔌 Test' }}
                </button>
                <button class="btn btn-sm st-btn-success" @click="registerPaybillCallbacks(pb)" :disabled="pb._registering" style="font-size:11px;padding:4px 10px;">
                  <span v-if="pb._registering" class="spinner-border spinner-border-sm me-1"></span>{{ pb._registering ? '...' : (pb.callbacksRegistered ? '🔄 Re-activate' : '🚀 Activate') }}
                </button>
                <button class="btn btn-sm" style="font-size:11px;padding:4px 10px;color:#4FC3F7;border:1px solid rgba(79,195,247,.3);border-radius:4px;" @click="testStkForPaybill(pb)" :disabled="pb._stkTesting">
                  <span v-if="pb._stkTesting" class="spinner-border spinner-border-sm me-1"></span>{{ pb._stkTesting ? '...' : '📲 Test STK' }}
                </button>
                <button v-if="!pb.isPrimary" class="btn btn-sm" style="font-size:11px;padding:4px 10px;color:#F59E0B;border:1px solid rgba(245,158,11,.3);border-radius:4px;" @click="setPrimaryPaybill(pb)">⭐ Set Primary</button>
              </div>
              <!-- Status messages -->
              <div v-if="pb._msg" style="padding:6px 10px;border-radius:6px;font-size:12px;margin-top:8px;" :style="{background: pb._msgOk ? 'rgba(16,185,129,.1)' : 'rgba(239,68,68,.1)', color: pb._msgOk ? '#10B981' : '#EF4444'}">{{ pb._msg }}</div>
            </div>

            <!-- Reset note -->
            <p v-if="paybills.length > 0" style="font-size:11px;color:#4A6A8A;margin-top:8px;">
              ⚠️ <strong>Need to reset?</strong> Go to <a href="https://developer.safaricom.co.ke" target="_blank" style="color:#4FC3F7;">Daraja Portal</a>, delete existing C2B URLs, then click "Re-activate".
            </p>
          </div>

          <!-- SMS CREDITS -->
          <div v-if="activeSection === 'sms_credits'" class="st-section">
            <div class="st-section-title"><i class="mdi mdi-message-text me-2"></i>SMS Credits</div>
            <div class="st-section-desc">Send notifications to parents. Top up your SMS balance via M-Pesa.</div>

            <!-- Balance Display -->
            <div style="background:linear-gradient(135deg,#0D2B5E,#1A3A6A);border-radius:12px;padding:20px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:center;">
              <div>
                <span style="font-size:12px;color:#8BA4C4;text-transform:uppercase;">SMS Balance</span>
                <div style="font-size:36px;font-weight:800;color:#4FC3F7;">{{ smsBalance }}</div>
                <span style="font-size:12px;color:#8BA4C4;">credits remaining</span>
              </div>
              <div style="font-size:48px;">📱</div>
            </div>

            <!-- Buy SMS Bundles -->
            <div style="background:#0A1628;border-radius:10px;padding:16px;margin-bottom:16px;">
              <h6 style="color:#4FC3F7;margin:0 0 12px;font-size:14px;">Top Up SMS Credits</h6>
              <div v-if="smsBundles.length === 0" style="color:#8BA4C4;font-size:13px;">Loading bundles...</div>
              <div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:12px;">
                <div v-for="b in smsBundles" :key="b.id"
                     @click="selectedBundle = b"
                     :style="{
                  flex: '1', minWidth: '130px', padding: '14px', borderRadius: '10px', cursor: 'pointer', textAlign: 'center', transition: '.2s',
                  background: selectedBundle?.id === b.id ? 'rgba(46,134,222,.15)' : '#0F2847',
                  border: selectedBundle?.id === b.id ? '2px solid #4FC3F7' : '2px solid transparent'
                }">
                  <div style="font-size:20px;font-weight:700;color:#E8EDF4;">{{ Number(b.sms_count).toLocaleString() }}</div>
                  <div style="font-size:11px;color:#8BA4C4;">SMS credits</div>
                  <div style="font-size:16px;font-weight:700;color:#10B981;margin-top:4px;">KSh {{ Number(b.price_ksh).toLocaleString() }}</div>
                </div>
              </div>
              <div v-if="selectedBundle" style="margin-bottom:12px;">
                <div class="d-flex gap-2 align-items-end flex-wrap">
                  <div>
                    <label class="st-label" style="font-size:11px;">M-Pesa Phone</label>
                    <input class="st-input" v-model="smsTopUpPhone" placeholder="0712345678" style="width:180px;" />
                  </div>
                  <button class="btn st-btn-primary btn-sm" @click="buySmsBundle" :disabled="smsBuying" style="white-space:nowrap;">
                    <span v-if="smsBuying" class="spinner-border spinner-border-sm me-1"></span>
                    {{ smsBuying ? 'Sending...' : '💳 Pay KSh ' + Number(selectedBundle.price_ksh).toLocaleString() }}
                  </button>
                </div>
              </div>
              <div v-if="smsBuyResult" style="padding:8px;border-radius:6px;font-size:12px;margin-top:4px;" :style="{background: smsBuyOk ? 'rgba(16,185,129,.1)' : 'rgba(239,68,68,.1)', color: smsBuyOk ? '#10B981' : '#EF4444'}">{{ smsBuyResult }}</div>
            </div>

            <!-- Test SMS -->
            <div style="background:#0A1628;border-radius:10px;padding:16px;">
              <h6 style="color:#4FC3F7;margin:0 0 12px;font-size:14px;">📤 Test SMS</h6>
              <p style="font-size:12px;color:#8BA4C4;margin-bottom:8px;">Send a test message to verify SMS delivery. This will deduct 1 SMS credit.</p>
              <div class="row g-3 mb-3">
                <div class="col-md-5">
                  <label class="st-label" style="font-size:11px;">Phone Number</label>
                  <input class="st-input" v-model="testSmsPhone" placeholder="0712345678" />
                </div>
                <div class="col-md-7">
                  <label class="st-label" style="font-size:11px;">Message (optional)</label>
                  <input class="st-input" v-model="testSmsMessage" placeholder="Default test message will be sent" />
                </div>
              </div>
              <button class="btn st-btn-primary btn-sm" @click="sendTestSms" :disabled="testSmsSending">
                <span v-if="testSmsSending" class="spinner-border spinner-border-sm me-1"></span>
                {{ testSmsSending ? 'Sending...' : '📤 Send Test SMS' }}
              </button>
              <div v-if="testSmsResult" style="padding:8px;border-radius:6px;font-size:12px;margin-top:8px;" :style="{background: testSmsOk ? 'rgba(16,185,129,.1)' : 'rgba(239,68,68,.1)', color: testSmsOk ? '#10B981' : '#EF4444'}">{{ testSmsResult }}</div>
            </div>
          </div>

          <!-- MODULE TOGGLES -->
          <div v-if="activeSection === 'modules'" class="st-section">
            <div class="st-section-title"><i class="mdi mdi-view-grid me-2"></i>Module Toggles</div>
            <div class="st-section-desc">Enable or disable platform modules for your institution.</div>
            <div class="st-alert st-alert-success" v-if="saveMsg === 'modules'">Settings saved!</div>
            <div class="st-module-grid">
              <div class="st-module-card" v-for="m in moduleList" :key="m.key">
                <div class="d-flex align-items-center gap-3">
                  <div class="st-module-icon" :style="{background: m.color + '20', color: m.color}"><i class="mdi" :class="m.icon"></i></div>
                  <div class="flex-fill">
                    <div class="st-module-name">{{ m.label }}</div>
                    <div class="st-module-desc">{{ m.desc }}</div>
                  </div>
                  <label class="st-toggle">
                    <input type="checkbox" v-model="modules[m.key]" />
                    <span class="st-toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
            <button class="btn st-btn-primary mt-3" @click="saveModules" :disabled="saving"><span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>Save Module Settings</button>
          </div>

          <!-- PERMISSION CATEGORIES -->
          <div v-if="activeSection === 'roles'" class="st-section">
            <div class="st-section-title"><i class="mdi mdi-shield-account me-2"></i>Permission Categories</div>
            <div class="st-section-desc">Create categories like "Cashier" or "Bursar", tick what each can do, then assign people to a category from the Users screen. Takes effect on next login.</div>
            <div class="st-alert st-alert-success" v-if="roleMsg">{{ roleMsg }}</div>

            <div class="row g-3">
              <div class="col-md-3">
                <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
                  <label class="st-label" style="margin:0;">Categories</label>
                  <button class="btn btn-sm st-btn-primary" style="font-size:11px;padding:4px 10px;" @click="newCategory">+ New</button>
                </div>
                <div v-if="!permGroups.length" style="padding:14px;text-align:center;color:#8BA4C4;font-size:12px;border:1px dashed #1A3A6A;border-radius:8px;">
                  No categories yet — click <strong>+ New</strong> to create one.
                </div>
                <div style="display:flex;flex-direction:column;gap:6px;">
                  <button v-for="g in permGroups" :key="g.id"
                    class="btn btn-sm"
                    :style="{
                      background: selectedGroupId === g.id ? '#4FC3F7' : '#0A1628',
                      color: selectedGroupId === g.id ? '#0A1628' : '#E8EDF4',
                      border: '1px solid #1A3A6A',
                      textAlign: 'left',
                      padding: '8px 12px',
                      fontWeight: 600
                    }"
                    @click="loadPermGroup(g.id)">
                    <div style="display:flex;align-items:center;justify-content:space-between;gap:6px;">
                      <span>{{ g.name }}</span>
                      <span style="font-size:10px;opacity:.7;">{{ g.user_count }}u · {{ g.menu_count }}m</span>
                    </div>
                    <div v-if="g.description" style="font-size:10px;font-weight:400;opacity:.75;margin-top:2px;">{{ g.description }}</div>
                  </button>
                </div>
              </div>
              <div class="col-md-9">
                <div v-if="!selectedGroupId && !creatingGroup" style="padding:32px;text-align:center;color:#8BA4C4;">
                  <i class="mdi mdi-arrow-left" style="font-size:24px;"></i>
                  <div>Select a category to edit, or click <strong>+ New</strong> to create one.</div>
                </div>
                <div v-else>
                  <div class="row g-2 mb-3">
                    <div class="col-md-4">
                      <label class="st-label">Category Name *</label>
                      <input class="st-input" v-model="permGroupForm.name" placeholder="e.g. Cashier, Bus Operator" />
                    </div>
                    <div class="col-md-5">
                      <label class="st-label">Description</label>
                      <input class="st-input" v-model="permGroupForm.description" placeholder="What this category can do" />
                    </div>
                    <div class="col-md-3">
                      <label class="st-label">
                        Base Role
                        <small style="color:#8BA4C4;font-weight:400;">(controls API permissions)</small>
                      </label>
                      <select class="st-select" v-model="permGroupForm.default_role" :disabled="!!permGroupForm.is_system">
                        <option value="cashier">Cashier</option>
                        <option value="dining_manager">Dining Manager</option>
                        <option value="bus_manager">Bus Manager</option>
                        <option value="class_teacher">Class Teacher</option>
                        <option value="bursar">Bursar</option>
                        <option value="campus_manager">Campus Manager</option>
                        <option value="super_admin">Super Admin</option>
                        <option value="platform_owner">Platform Owner</option>
                      </select>
                      <small v-if="permGroupForm.is_system" style="color:#8BA4C4;font-size:10px;">System categories: base role locked.</small>
                    </div>
                  </div>

                  <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
                    <div style="font-size:12px;color:#8BA4C4;">
                      Tick the menus that people in this category can access.
                    </div>
                    <div>
                      <button v-if="selectedGroupId && !permGroupForm.is_system" class="btn btn-sm" style="font-size:11px;color:#EF4444;border:1px solid rgba(239,68,68,.3);margin-right:6px;" @click="deletePermGroup" :disabled="savingRolePerms">🗑 Delete</button>
                      <button class="btn btn-sm" style="font-size:11px;color:#8BA4C4;border:1px solid #1A3A6A;margin-right:6px;" @click="cancelPermGroup">Cancel</button>
                      <button class="btn st-btn-primary btn-sm" style="font-size:11px;" @click="savePermGroup" :disabled="savingRolePerms">
                        <span v-if="savingRolePerms" class="spinner-border spinner-border-sm me-1"></span>Save Category
                      </button>
                    </div>
                  </div>

                  <div v-for="(group, gName) in groupedMenus" :key="gName" style="margin-bottom:14px;background:#0A1628;border-radius:8px;padding:10px 14px;border-left:3px solid #4FC3F7;">
                    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
                      <div style="font-size:11px;font-weight:700;color:#4FC3F7;text-transform:uppercase;letter-spacing:.5px;">{{ gName }}</div>
                      <div>
                        <a href="javascript:void(0)" style="font-size:10px;color:#8BA4C4;margin-right:8px;" @click="toggleGroupAll(gName, true)">All</a>
                        <a href="javascript:void(0)" style="font-size:10px;color:#8BA4C4;" @click="toggleGroupAll(gName, false)">None</a>
                      </div>
                    </div>
                    <div v-for="m in group" :key="m.key" style="margin-bottom:6px;padding-bottom:6px;border-bottom:1px dashed rgba(79,195,247,.1);">
                      <label style="display:flex;align-items:center;gap:8px;cursor:pointer;color:#E8EDF4;font-size:13px;font-weight:600;">
                        <input type="checkbox" v-model="m.can_access" @change="onMenuToggle(m)" />
                        <span>{{ m.label }}</span>
                        <span v-if="m.can_access && actionsForMenu(m.key).length" style="font-size:10px;color:#8BA4C4;font-weight:400;">— {{ countAllowedActions(m.key) }}/{{ actionsForMenu(m.key).length }} actions</span>
                      </label>
                      <!-- Nested actions: only visible when the menu is granted and there are actions for it -->
                      <div v-if="m.can_access && actionsForMenu(m.key).length" style="margin-left:24px;margin-top:6px;padding:6px 10px;background:rgba(46,134,222,.05);border-left:2px solid rgba(79,195,247,.25);border-radius:4px;">
                        <div style="display:flex;justify-content:space-between;margin-bottom:4px;">
                          <div style="font-size:9px;color:#8BA4C4;text-transform:uppercase;letter-spacing:.5px;font-weight:700;">What they can do</div>
                          <div>
                            <a href="javascript:void(0)" style="font-size:9px;color:#8BA4C4;margin-right:6px;" @click="toggleMenuActions(m.key, true)">All</a>
                            <a href="javascript:void(0)" style="font-size:9px;color:#8BA4C4;" @click="toggleMenuActions(m.key, false)">None</a>
                          </div>
                        </div>
                        <div class="row g-1">
                          <div class="col-md-6" v-for="a in actionsForMenu(m.key)" :key="a.key">
                            <label style="display:flex;align-items:center;gap:6px;cursor:pointer;color:#E8EDF4;font-size:12px;font-weight:400;">
                              <input type="checkbox" v-model="a.allowed" />
                              <span>{{ a.label }}</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- GRADING SYSTEMS -->
          <div v-if="activeSection === 'grading'" class="st-section">
            <div class="st-section-title"><i class="mdi mdi-chart-bell-curve-cumulative me-2"></i>Grading Systems</div>
            <div class="st-section-desc">Configure how grades are entered and reported. Pick from built-in presets (CBC, 8-4-4, TVET CBET, GPA, IB, A-Level…) or build your own.</div>
            <div class="st-alert st-alert-success" v-if="gradeMsg">{{ gradeMsg }}</div>
            <div class="st-alert st-alert-danger" v-if="gradeError">{{ gradeError }}</div>

            <div class="row g-2 mb-3">
              <div class="col-md-6">
                <label class="st-label">Education type (drives default presets + UI vocabulary)</label>
                <select class="st-select" v-model="institutionEducationType" @change="saveEducationType">
                  <option value="pre_primary">Pre-Primary (PP1, PP2)</option>
                  <option value="primary">Primary School (Grade 1–6)</option>
                  <option value="junior_school">Junior School (Grade 7–9)</option>
                  <option value="secondary">Secondary School / High School</option>
                  <option value="mixed_school">Mixed School (covers multiple K-12 levels)</option>
                  <option value="tvet">TVET / Technical Institute</option>
                  <option value="college">College (Diploma / Certificate)</option>
                  <option value="university">University</option>
                  <option value="mixed_tertiary">Mixed Tertiary (university + colleges)</option>
                </select>
              </div>
              <div class="col-md-6 d-flex align-items-end">
                <button class="btn st-btn-primary" @click="showApplyPreset = true"><i class="mdi mdi-import me-1"></i>Apply a Preset</button>
              </div>
            </div>

            <div v-if="!gradingSystems.length" style="padding:16px;text-align:center;color:#8BA4C4;border:1px dashed #1A3A6A;border-radius:8px;">
              No grading systems yet. Click <strong>Apply a Preset</strong> to start with a ready-made template.
            </div>
            <div v-else class="row g-2">
              <div class="col-md-6" v-for="g in gradingSystems" :key="g.id">
                <div class="st-grading-card" :class="{active: selectedGradingId === g.id}" @click="loadGradingSystem(g.id)">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <div style="color:#E8EDF4;font-weight:700;">
                        {{ g.name }}
                        <i v-if="g.is_default == 1" class="mdi mdi-star" style="color:#F59E0B;"></i>
                      </div>
                      <div style="color:#8BA4C4;font-size:11px;margin-top:2px;">{{ scaleLabel(g.scale_type) }} · {{ g.band_count }} bands · {{ g.component_count }} components</div>
                    </div>
                    <span v-if="g.is_observation_based == 1" class="st-badge" style="background:rgba(168,85,247,.15);color:#A855F7;border:1px solid rgba(168,85,247,.3);font-size:10px;">Observe</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Editor -->
            <div v-if="selectedGradingId" class="st-card" style="margin-top:14px;">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div style="color:#E8EDF4;font-weight:700;">Edit grading system</div>
                <div>
                  <button v-if="gradingForm.is_default != 1" class="btn st-btn-sm st-btn-blue me-2" @click="setDefault(selectedGradingId)">Set as default</button>
                  <button class="btn st-btn-sm" style="color:#EF4444;border:1px solid rgba(239,68,68,.3);" @click="deleteGradingSystem(selectedGradingId)">Delete</button>
                </div>
              </div>

              <div class="row g-2 mb-3">
                <div class="col-md-6"><label class="st-label">Name</label><input class="st-input" v-model="gradingForm.name" /></div>
                <div class="col-md-6"><label class="st-label">Description</label><input class="st-input" v-model="gradingForm.description" /></div>
              </div>
              <div class="row g-2 mb-3">
                <div class="col-md-3"><label class="st-label">Scale</label>
                  <select class="st-select" v-model="gradingForm.scale_type">
                    <option value="performance_level">Performance Level</option>
                    <option value="letter">Letter</option>
                    <option value="percentage">Percentage</option>
                    <option value="gpa">GPA</option>
                    <option value="points">Points</option>
                    <option value="pass_fail">Pass / Fail</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div class="col-md-3"><label class="st-label">Aggregation</label>
                  <select class="st-select" v-model="gradingForm.aggregation">
                    <option value="weighted_average">Weighted average</option>
                    <option value="average">Average</option>
                    <option value="sum">Sum</option>
                    <option value="min">Minimum</option>
                    <option value="max">Maximum</option>
                  </select>
                </div>
                <div class="col-md-3"><label class="st-label">Award type</label>
                  <select class="st-select" v-model="gradingForm.award_type">
                    <option value="none">None</option>
                    <option value="artisan">Artisan (KNQF 4)</option>
                    <option value="certificate">Certificate (KNQF 5)</option>
                    <option value="diploma">Diploma (KNQF 6)</option>
                    <option value="degree">Degree</option>
                  </select>
                </div>
                <div class="col-md-3" v-if="gradingForm.scale_type === 'gpa'">
                  <label class="st-label">GPA max</label>
                  <input type="number" min="1" step="0.1" class="st-input" v-model.number="gradingForm.gpa_max" />
                </div>
                <div class="col-md-3 d-flex align-items-end">
                  <label style="display:flex;align-items:center;gap:8px;cursor:pointer;color:#E8EDF4;font-size:13px;">
                    <input type="checkbox" v-model="gradingForm.is_observation_based" /> Observation-based
                  </label>
                </div>
              </div>

              <div style="font-size:11px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;margin:10px 0 6px;">Bands</div>
              <div v-for="(b, bi) in gradingForm.bands" :key="bi" class="row g-2 mb-2 align-items-end">
                <div class="col-md-3"><label class="st-label">Name</label><input class="st-input" v-model="b.name" /></div>
                <div class="col-md-1"><label class="st-label">Code</label><input class="st-input" v-model="b.code" /></div>
                <div class="col-md-2"><label class="st-label">Min</label><input type="number" step="0.01" class="st-input" v-model.number="b.min_value" /></div>
                <div class="col-md-2"><label class="st-label">Max</label><input type="number" step="0.01" class="st-input" v-model.number="b.max_value" /></div>
                <div class="col-md-2"><label class="st-label">Points</label><input type="number" step="0.01" class="st-input" v-model.number="b.points" /></div>
                <div class="col-md-1"><label class="st-label">Color</label><input class="st-input" v-model="b.color" placeholder="#10B981" /></div>
                <div class="col-md-1"><button class="btn st-btn-sm" style="color:#EF4444;" @click="gradingForm.bands.splice(bi,1)"><i class="mdi mdi-trash-can"></i></button></div>
              </div>
              <button class="btn st-btn-sm st-btn-blue" @click="gradingForm.bands.push({name:'',code:'',min_value:0,max_value:100,points:null,color:''})"><i class="mdi mdi-plus me-1"></i>Add band</button>

              <div style="font-size:11px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;margin:14px 0 6px;">Components <small style="text-transform:none;font-weight:400;">(weighted parts: CAT 30% + Exam 70%, etc.)</small></div>
              <div v-for="(c, ci) in gradingForm.components" :key="'c'+ci" class="row g-2 mb-2 align-items-end">
                <div class="col-md-5"><label class="st-label">Name</label><input class="st-input" v-model="c.name" /></div>
                <div class="col-md-3"><label class="st-label">Code</label><input class="st-input" v-model="c.code" /></div>
                <div class="col-md-3"><label class="st-label">Weight %</label><input type="number" min="0" max="100" step="0.01" class="st-input" v-model.number="c.weight_pct" /></div>
                <div class="col-md-1"><button class="btn st-btn-sm" style="color:#EF4444;" @click="gradingForm.components.splice(ci,1)"><i class="mdi mdi-trash-can"></i></button></div>
              </div>
              <button class="btn st-btn-sm st-btn-blue" @click="gradingForm.components.push({name:'',code:'',weight_pct:0})"><i class="mdi mdi-plus me-1"></i>Add component</button>

              <div class="d-flex gap-2 mt-3">
                <button class="btn st-btn-primary" @click="saveGradingSystem" :disabled="savingGrading">
                  <span v-if="savingGrading" class="spinner-border spinner-border-sm me-1"></span>{{ savingGrading ? 'Saving...' : 'Save' }}
                </button>
                <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="selectedGradingId = null">Close</button>
              </div>
            </div>
          </div>

          <!-- APPLY PRESET MODAL -->
          <div class="st-modal-overlay" v-if="showApplyPreset" @click.self="showApplyPreset = false">
            <div class="st-modal" style="max-width:640px;">
              <div class="st-modal-title">
                <span><i class="mdi mdi-import me-2"></i>Apply a Grading Preset</span>
                <button class="st-modal-close" @click="showApplyPreset = false">&times;</button>
              </div>
              <div class="st-alert st-alert-danger" v-if="presetError">{{ presetError }}</div>
              <div v-if="suggestedPresetKeys.length" style="font-size:11px;color:#10B981;text-transform:uppercase;font-weight:700;letter-spacing:.5px;margin-bottom:6px;">Suggested for your institution type</div>
              <div v-for="p in sortedPresets" :key="p.preset_key" class="st-preset-row" :class="{suggested: suggestedPresetKeys.includes(p.preset_key)}" @click="applyPreset(p.preset_key)">
                <div>
                  <div style="color:#E8EDF4;font-weight:700;">{{ p.name }} <span v-if="suggestedPresetKeys.includes(p.preset_key)" class="st-badge st-badge-success" style="font-size:10px;margin-left:6px;">Suggested</span></div>
                  <div style="color:#8BA4C4;font-size:11px;margin-top:2px;">{{ p.description }}</div>
                </div>
                <button class="btn st-btn-sm st-btn-blue" @click.stop="applyPreset(p.preset_key)">Apply</button>
              </div>
            </div>
          </div>

          <!-- ATTENDANCE RULES -->
          <div v-if="activeSection === 'attendance'" class="st-section">
            <div class="st-section-title"><i class="mdi mdi-clock-check me-2"></i>Attendance Rules</div>
            <div class="st-section-desc">Configure attendance tracking behaviour and thresholds.</div>
            <div class="st-alert st-alert-success" v-if="saveMsg === 'attendance'">Settings saved!</div>
            <div class="row g-3 mb-3">
              <div class="col-md-6"><label class="st-label">School Start Time</label><input type="time" class="st-input" v-model="attendance.startTime" /></div>
              <div class="col-md-6"><label class="st-label">School End Time</label><input type="time" class="st-input" v-model="attendance.endTime" /></div>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-md-6"><label class="st-label">Late After (minutes from start)</label><input type="number" class="st-input" v-model="attendance.lateThreshold" min="0" /></div>
              <div class="col-md-6"><label class="st-label">Auto Clock-Out Time</label><input type="time" class="st-input" v-model="attendance.autoClockOut" /></div>
            </div>
            <div class="mb-3">
              <label class="st-label d-flex align-items-center gap-2">
                <input type="checkbox" v-model="attendance.notifyParentOnLate" style="width:18px;height:18px;accent-color:#2E86DE;" />
                Notify parents when student arrives late
              </label>
            </div>
            <div class="mb-3">
              <label class="st-label d-flex align-items-center gap-2">
                <input type="checkbox" v-model="attendance.notifyParentOnAbsent" style="width:18px;height:18px;accent-color:#2E86DE;" />
                Notify parents when student is absent (no tap by end of day)
              </label>
            </div>
            <div class="mb-3">
              <label class="st-label d-flex align-items-center gap-2">
                <input type="checkbox" v-model="attendance.autoDetectDirection" style="width:18px;height:18px;accent-color:#2E86DE;" />
                Auto-detect clock-in vs clock-out (based on last tap)
              </label>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="st-label">Weekend Days</label>
                <select class="st-select" v-model="attendance.weekendDays">
                  <option value="sat,sun">Saturday & Sunday</option>
                  <option value="fri,sat">Friday & Saturday</option>
                  <option value="sun">Sunday Only</option>
                </select>
              </div>
              <div class="col-md-6"><label class="st-label">Min Tap Interval (seconds)</label><input type="number" class="st-input" v-model="attendance.minTapInterval" min="5" placeholder="30" /></div>
            </div>
            <button class="btn st-btn-primary" @click="saveAttendance" :disabled="saving"><span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>Save Attendance Rules</button>
          </div>

          <!-- FEE SETTINGS -->
          <div v-if="activeSection === 'fees'" class="st-section">
            <div class="st-section-title"><i class="mdi mdi-cash-multiple me-2"></i>Fee Structure Settings</div>
            <div class="st-section-desc">Configure fee payment rules, deadlines and penalties.</div>
            <div class="st-alert st-alert-success" v-if="saveMsg === 'fees'">Settings saved!</div>
            <div class="row g-3 mb-3">
              <div class="col-md-6"><label class="st-label">Currency</label><input class="st-input" v-model="fees.currency" placeholder="KSh" /></div>
              <div class="col-md-6"><label class="st-label">Payment Deadline (days into term)</label><input type="number" class="st-input" v-model="fees.paymentDeadlineDays" min="0" /></div>
            </div>
            <div class="mb-3">
              <label class="st-label d-flex align-items-center gap-2">
                <input type="checkbox" v-model="fees.enableLatePenalty" style="width:18px;height:18px;accent-color:#2E86DE;" />
                Enable late payment penalty
              </label>
            </div>
            <div class="row g-3 mb-3" v-if="fees.enableLatePenalty">
              <div class="col-md-6">
                <label class="st-label">Penalty Type</label>
                <select class="st-select" v-model="fees.penaltyType">
                  <option value="fixed">Fixed Amount</option>
                  <option value="percentage">Percentage of Balance</option>
                </select>
              </div>
              <div class="col-md-6"><label class="st-label">Penalty Amount / %</label><input type="number" class="st-input" v-model="fees.penaltyAmount" min="0" /></div>
            </div>
            <div class="mb-3">
              <label class="st-label d-flex align-items-center gap-2">
                <input type="checkbox" v-model="fees.sendFeeReminders" style="width:18px;height:18px;accent-color:#2E86DE;" />
                Send automatic fee reminders to parents
              </label>
            </div>
            <div class="row g-3 mb-3" v-if="fees.sendFeeReminders">
              <div class="col-md-6"><label class="st-label">Reminder Days Before Deadline</label><input class="st-input" v-model="fees.reminderDays" placeholder="7,3,1" /></div>
              <div class="col-md-6">
                <label class="st-label">Reminder Channel</label>
                <select class="st-select" v-model="fees.reminderChannel">
                  <option value="sms">SMS Only</option>
                  <option value="email">Email Only</option>
                  <option value="both">SMS & Email</option>
                </select>
              </div>
            </div>
            <button class="btn st-btn-primary" @click="saveFees" :disabled="saving"><span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>Save Fee Settings</button>
          </div>

          <!-- BILLING & SUBSCRIPTION -->
          <div v-if="activeSection === 'billing'" class="st-section">
            <div class="st-section-title"><i class="mdi mdi-credit-card me-2"></i>Billing & Subscription</div>
            <div class="st-section-desc">Your subscription plan, usage, and billing history.</div>
            <div class="st-plan-card">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <div class="st-plan-name">{{ billing.planName || 'Free Plan' }}</div>
                  <div class="st-plan-status">Status: <span :class="billing.status === 'active' ? 'text-success' : 'text-warning'">{{ billing.status || 'Active' }}</span></div>
                </div>
                <div class="text-end">
                  <div style="color:#8BA4C4;font-size:12px;">Monthly</div>
                  <div style="color:#4FC3F7;font-size:28px;font-weight:800;">KSh {{ billing.monthlyFee || '0' }}</div>
                </div>
              </div>
            </div>
            <div class="st-usage-grid">
              <div class="st-usage-item">
                <div class="st-usage-label">Students</div>
                <div class="st-usage-val">{{ billing.studentCount || '0' }} <span>/ {{ billing.studentLimit || '∞' }}</span></div>
              </div>
              <div class="st-usage-item">
                <div class="st-usage-label">SMS Sent (this month)</div>
                <div class="st-usage-val">{{ billing.smsSentMonth || '0' }}</div>
              </div>
              <div class="st-usage-item">
                <div class="st-usage-label">Transactions (this month)</div>
                <div class="st-usage-val">{{ billing.transactionsMonth || '0' }}</div>
              </div>
              <div class="st-usage-item">
                <div class="st-usage-label">Storage Used</div>
                <div class="st-usage-val">{{ billing.storageUsed || '0 MB' }}</div>
              </div>
            </div>
            <div class="st-section-title mt-4" style="font-size:15px;"><i class="mdi mdi-history me-1"></i>Billing History</div>
            <div v-if="billing.invoices && billing.invoices.length" style="overflow-x:auto;">
              <table class="st-table">
                <thead><tr><th>Date</th><th>Description</th><th>Amount</th><th>Status</th></tr></thead>
                <tbody>
                <tr v-for="inv in billing.invoices" :key="inv.id">
                  <td>{{ inv.date }}</td>
                  <td>{{ inv.description }}</td>
                  <td>KSh {{ inv.amount }}</td>
                  <td><span class="st-badge" :class="inv.status === 'paid' ? 'st-badge-success' : 'st-badge-warning'">{{ inv.status }}</span></td>
                </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="st-empty-sm">No billing history yet.</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: "SettingsPage",
  components: { Navigation },
  data() {
    return {
      loading: true,
      saving: false,
      saveMsg: '',
      activeSection: 'profile',
      smsBalance: 0,
      sections: [
        { id: 'profile', label: 'Institution Profile', icon: 'mdi-domain' },
        { id: 'mpesa', label: 'M-Pesa', icon: 'mdi-cellphone' },
        { id: 'sms_credits', label: 'SMS Credits', icon: 'mdi-message-text' },
        { id: 'modules', label: 'Module Toggles', icon: 'mdi-view-grid' },
        { id: 'roles', label: 'Permission Categories', icon: 'mdi-shield-account' },
        { id: 'grading', label: 'Grading Systems', icon: 'mdi-chart-bell-curve-cumulative' },
        { id: 'attendance', label: 'Attendance Rules', icon: 'mdi-clock-check' },
        { id: 'fees', label: 'Fee Settings', icon: 'mdi-cash-multiple' },
        { id: 'billing', label: 'Billing & Subscription', icon: 'mdi-credit-card' },
      ],
      // Grading systems
      gradingSystems: [], gradingPresets: [], selectedGradingId: null,
      gradingForm: { name: '', description: '', scale_type: 'percentage', aggregation: 'weighted_average', award_type: 'none', gpa_max: null, is_observation_based: false, bands: [], components: [] },
      institutionEducationType: sessionStorage.getItem('educationType') || 'mixed_school',
      suggestedPresetKeys: [],
      showApplyPreset: false, savingGrading: false, gradeMsg: '', gradeError: '', presetError: '',
      // Permission Categories
      permGroups: [], selectedGroupId: null, creatingGroup: false,
      permGroupForm: { id: null, name: '', description: '', default_role: 'cashier', is_system: 0 },
      rolePermMenus: [], rolePermActions: [], savingRolePerms: false, roleMsg: '',
      profile: { name: '', type: 'secondary', email: '', phone: '', address: '', county: '', country: 'Kenya', website: '', motto: '', logo: '/assets/images/simi_trac_logo.png', principalSignature: '', officialStamp: '' },
      logoUploading: false,
      fileUploading: false,
      sms: { atUsername: '', atApiKey: '', atSenderId: '', atEnvironment: 'sandbox' },
      email: { smtpHost: '', smtpPort: '2525', smtpUsername: '', smtpPassword: '', fromEmail: '', fromName: 'Simi Trac', smtpEncryption: 'tls' },
      paybills: [], showAddPaybill: false, editingPaybill: null, showSetupGuide: false,
      pbForm: { shortcode: '', tillNumber: '', accountPrefix: '', label: 'M-Pesa', purpose: 'all', type: 'paybill', consumerKey: '', consumerSecret: '', passkey: '', environment: 'production', isPrimary: false, initiatorName: '', securityCredential: '' },
      // SMS Credits
      smsBundles: [], selectedBundle: null, smsTopUpPhone: '', smsBuying: false, smsBuyResult: '', smsBuyOk: false,
      testSmsPhone: '', testSmsMessage: '', testSmsSending: false, testSmsResult: '', testSmsOk: false,
      modules: { attendance: true, staffAttendance: true, lessonAttendance: true, timetable: true, pos: true, wallets: true, fees: true, dining: true, kitchen: true, discipline: true, bulkSms: true, buses: true, events: true, payments: true, parentPortal: true, chores: false, cashOut: true },
      moduleList: [
        { key: 'attendance', label: 'Student Attendance', desc: 'NFC card tapping at gates, GPS tracking', icon: 'mdi-map-marker-radius', color: '#10B981' },
        { key: 'staffAttendance', label: 'Staff Attendance', desc: 'Daily staff check-in/out via NFC or manual', icon: 'mdi-account-clock', color: '#0EA5E9' },
        { key: 'lessonAttendance', label: 'Lesson Attendance', desc: 'Teacher marks students per subject/period', icon: 'mdi-clipboard-check', color: '#8B5CF6' },
        { key: 'timetable', label: 'Timetable & Subjects', desc: 'Class schedules, subjects, teacher assignments', icon: 'mdi-calendar-clock', color: '#6366F1' },
        { key: 'pos', label: 'POS Terminal', desc: 'Cashless dining payments', icon: 'mdi-point-of-sale', color: '#F59E0B' },
        { key: 'wallets', label: 'Student & Staff Wallets', desc: 'Digital wallet management, top-ups', icon: 'mdi-wallet', color: '#2E86DE' },
        { key: 'fees', label: 'School Fees', desc: 'Fee tracking, billing, M-Pesa payments', icon: 'mdi-school', color: '#EF4444' },
        { key: 'payments', label: 'Payment Management', desc: 'M-Pesa reconciliation, verify, reverse', icon: 'mdi-credit-card-check', color: '#10B981' },
        { key: 'dining', label: 'Dining Management', desc: 'Menu, outlets, meal tracking', icon: 'mdi-food', color: '#FB923C' },
        { key: 'kitchen', label: 'Kitchen Orders', desc: 'KDS display, order tracking, kitchen printer', icon: 'mdi-stove', color: '#F97316' },
        { key: 'events', label: 'Events', desc: 'Sports days, trips, concerts — attendance, payments, prizes', icon: 'mdi-calendar-star', color: '#EC4899' },
        { key: 'discipline', label: 'Discipline', desc: 'Incident logging and parent notifications', icon: 'mdi-gavel', color: '#A855F7' },
        { key: 'bulkSms', label: 'Bulk SMS', desc: 'Mass messaging to parents', icon: 'mdi-message-text-fast', color: '#06B6D4' },
        { key: 'buses', label: 'Buses & Routes', desc: 'Transport and route management', icon: 'mdi-bus', color: '#8B5CF6' },
        { key: 'parentPortal', label: 'Parent Portal', desc: 'Parent app — wallet, fees, attendance, chat', icon: 'mdi-account-child', color: '#F472B6' },
        { key: 'chores', label: 'Home Chores', desc: 'Parent-assigned tasks with rewards', icon: 'mdi-broom', color: '#EC4899' },
        { key: 'cashOut', label: 'Cash Out', desc: 'Students withdraw with parent approval', icon: 'mdi-cash-refund', color: '#14B8A6' },
      ],
      attendance: { startTime: '07:30', endTime: '16:00', lateThreshold: 30, autoClockOut: '17:00', notifyParentOnLate: true, notifyParentOnAbsent: true, autoDetectDirection: true, weekendDays: 'sat,sun', minTapInterval: 30 },
      fees: { currency: 'KSh', paymentDeadlineDays: 14, enableLatePenalty: false, penaltyType: 'fixed', penaltyAmount: 500, sendFeeReminders: true, reminderDays: '7,3,1', reminderChannel: 'both' },
      billing: { planName: 'Free Plan', status: 'active', monthlyFee: '0', studentCount: 0, studentLimit: '50', smsSentMonth: 0, transactionsMonth: 0, storageUsed: '0 MB', invoices: [] },
    };
  },
  mounted() { this.loadSettings(); this.loadSmsBundles(); this.loadPermGroups(); this.loadGradingSystems(); this.loadGradingPresets(); },
  computed: {
    groupedMenus() {
      const groups = {};
      for (const m of this.rolePermMenus) {
        if (!groups[m.group]) groups[m.group] = [];
        groups[m.group].push(m);
      }
      return groups;
    },
    sortedPresets() {
      // Suggested first, then everything else alphabetically
      const suggested = this.suggestedPresetKeys || [];
      const list = (this.gradingPresets || []).slice();
      return list.sort((a, b) => {
        const aS = suggested.includes(a.preset_key) ? 0 : 1;
        const bS = suggested.includes(b.preset_key) ? 0 : 1;
        if (aS !== bS) return aS - bS;
        return (a.name || '').localeCompare(b.name || '');
      });
    },
  },
  methods: {
    // ========== Grading Systems ==========
    scaleLabel(s) {
      return ({
        performance_level: 'Performance Levels',
        letter: 'Letter Grades',
        percentage: 'Percentage',
        gpa: 'GPA',
        points: 'Points',
        pass_fail: 'Pass / Fail',
        custom: 'Custom',
      })[s] || s;
    },
    async loadGradingSystems() {
      try {
        const r = await executeJson({ function: 'listGradingSystems' }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.gradingSystems = d.systems || [];
        }
      } catch (e) { /* silent */ }
    },
    async loadGradingPresets() {
      try {
        const r = await executeJson({ function: 'listGradingPresets' }, simitrack);
        if (r.data.success) this.gradingPresets = Array.isArray(r.data.data) ? r.data.data : [];
        // suggested keys for current education type — derive locally
        this.suggestedPresetKeys = this.deriveSuggested(this.institutionEducationType);
      } catch (e) {}
    },
    deriveSuggested(t) {
      const map = {
        pre_primary: ['cbc_pre_primary'],
        primary: ['cbc_primary','cbc_pre_primary'],
        junior_school: ['cbc_junior'],
        secondary: ['cbc_senior','kenya_844'],
        mixed_school: ['cbc_pre_primary','cbc_primary','cbc_junior','cbc_senior','kenya_844'],
        tvet: ['tvet_cbet_element','tvet_knec_unit'],
        college: ['tvet_knec_unit','tvet_cbet_element','kenya_uni_gpa_4'],
        university: ['kenya_uni_gpa_4','kenya_uni_gpa_5'],
        mixed_tertiary: ['kenya_uni_gpa_4','tvet_knec_unit','tvet_cbet_element'],
      };
      return map[t] || ['percentage'];
    },
    async saveEducationType() {
      try {
        const r = await executeJson({ function: 'setInstitutionEducationType', educationType: this.institutionEducationType }, simitrack);
        if (r.data.success) {
          sessionStorage.setItem('educationType', this.institutionEducationType);
          this.suggestedPresetKeys = this.deriveSuggested(this.institutionEducationType);
          this.gradeMsg = 'Education type saved';
          setTimeout(() => { this.gradeMsg = ''; }, 2500);
        }
      } catch (e) { this.gradeError = 'Failed to save education type'; setTimeout(() => { this.gradeError = ''; }, 3000); }
    },
    async loadGradingSystem(systemId) {
      this.selectedGradingId = systemId;
      try {
        const r = await executeJson({ function: 'getGradingSystem', systemId }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.gradingForm = {
            id: d.id,
            name: d.name || '',
            description: d.description || '',
            scale_type: d.scale_type || 'percentage',
            aggregation: d.aggregation || 'weighted_average',
            award_type: d.award_type || 'none',
            gpa_max: d.gpa_max ? parseFloat(d.gpa_max) : null,
            is_observation_based: d.is_observation_based == 1 || d.is_observation_based === true,
            is_default: d.is_default,
            bands: (d.bands || []).map(b => ({ ...b, min_value: parseFloat(b.min_value), max_value: parseFloat(b.max_value), points: b.points !== null ? parseFloat(b.points) : null })),
            components: (d.components || []).map(c => ({ ...c, weight_pct: parseFloat(c.weight_pct) })),
          };
        }
      } catch (e) { this.gradeError = 'Failed to load grading system'; }
    },
    async saveGradingSystem() {
      this.savingGrading = true;
      this.gradeError = ''; this.gradeMsg = '';
      try {
        const params = {
          function: 'saveGradingSystem',
          systemId: this.selectedGradingId,
          name: this.gradingForm.name,
          description: this.gradingForm.description,
          scaleType: this.gradingForm.scale_type,
          aggregation: this.gradingForm.aggregation,
          awardType: this.gradingForm.award_type,
          gpaMax: this.gradingForm.gpa_max,
          isObservationBased: this.gradingForm.is_observation_based ? 1 : 0,
          bands: this.gradingForm.bands.map(b => ({ name: b.name, code: b.code, minValue: b.min_value, maxValue: b.max_value, points: b.points, color: b.color, description: b.description })),
          components: this.gradingForm.components.map(c => ({ name: c.name, code: c.code, weightPct: c.weight_pct })),
        };
        const r = await executeJson(params, simitrack);
        if (r.data.success) {
          this.gradeMsg = 'Saved';
          await this.loadGradingSystems();
          setTimeout(() => { this.gradeMsg = ''; }, 2500);
        } else { this.gradeError = r.data.message || 'Save failed'; }
      } catch (e) { this.gradeError = 'Connection error'; }
      this.savingGrading = false;
    },
    async deleteGradingSystem(systemId) {
      if (!confirm('Delete this grading system? Cannot be undone if no grades reference it.')) return;
      try {
        const r = await executeJson({ function: 'deleteGradingSystem', systemId }, simitrack);
        if (r.data.success) {
          this.selectedGradingId = null;
          await this.loadGradingSystems();
        } else { alert(r.data.message || 'Delete failed'); }
      } catch (e) { alert('Connection error'); }
    },
    async setDefault(systemId) {
      try {
        const r = await executeJson({ function: 'setDefaultGradingSystem', systemId }, simitrack);
        if (r.data.success) await this.loadGradingSystems();
      } catch (e) {}
    },
    async applyPreset(presetKey) {
      this.presetError = '';
      try {
        const r = await executeJson({ function: 'applyGradingPreset', presetKey }, simitrack);
        if (r.data.success) {
          this.showApplyPreset = false;
          await this.loadGradingSystems();
          this.gradeMsg = 'Preset applied';
          setTimeout(() => { this.gradeMsg = ''; }, 2500);
        } else { this.presetError = r.data.message || 'Failed to apply preset'; }
      } catch (e) { this.presetError = 'Connection error'; }
    },

    async loadPermGroups() {
      try {
        const r = await executeJson({ function: 'listPermissionGroups' }, simitrack);
        if (r.data.success) this.permGroups = r.data.data || [];
      } catch (e) {}
    },
    async loadPermGroup(groupId) {
      this.selectedGroupId = groupId;
      this.creatingGroup = false;
      this.roleMsg = '';
      try {
        const r = await executeJson({ function: 'getPermissionGroup', groupId }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.permGroupForm = {
            id: d.group.id,
            name: d.group.name || '',
            description: d.group.description || '',
            default_role: d.group.default_role || 'cashier',
            is_system: d.group.is_system || 0,
          };
          this.rolePermMenus = d.menus || [];
          this.rolePermActions = d.actions || [];
        }
      } catch (e) { alert('Failed to load category'); }
    },
    newCategory() {
      this.selectedGroupId = null;
      this.creatingGroup = true;
      this.roleMsg = '';
      this.permGroupForm = { id: null, name: '', description: '', default_role: 'cashier', is_system: 0 };
      // Empty checkbox grid using current menu catalog (load from any group OR fall back to fetching one)
      // Cleanest: just clone the menu list with can_access=false. We use the catalog from the API via getPermissionGroup,
      // but since we don't have a group yet, fetch the catalog by re-using rolePermMenus from a previous load
      // or fetch fresh via getMenuCatalog.
      this.loadEmptyMenuCatalog();
    },
    async loadEmptyMenuCatalog() {
      try {
        const r = await executeJson({ function: 'getMenuCatalog' }, simitrack);
        if (r.data.success) {
          const menus = (r.data.data && r.data.data.menus) || [];
          this.rolePermMenus = menus.map(m => ({ ...m, can_access: false }));
          // For NEW categories, derive the action grid from the catalog (none allowed by default)
          this.rolePermActions = (r.data.data && r.data.data.actions) || [];
          if (!this.rolePermActions.length) {
            // If the catalog endpoint doesn't expose actions, load an empty group to grab the catalog
            try {
              const g = await executeJson({ function: 'listPermissionGroups' }, simitrack);
              if (g.data.success && g.data.data && g.data.data.length) {
                const sample = g.data.data[0];
                const r2 = await executeJson({ function: 'getPermissionGroup', groupId: sample.id }, simitrack);
                if (r2.data.success) {
                  this.rolePermActions = (r2.data.data && r2.data.data.actions || []).map(a => ({ ...a, allowed: false }));
                }
              }
            } catch (e) { /* silent */ }
          }
        }
      } catch (e) { alert('Failed to load menu catalog'); }
    },
    cancelPermGroup() {
      this.selectedGroupId = null;
      this.creatingGroup = false;
      this.permGroupForm = { id: null, name: '', description: '', default_role: 'cashier', is_system: 0 };
      this.rolePermMenus = [];
      this.rolePermActions = [];
    },
    toggleGroupAll(groupName, value) {
      this.rolePermMenus = this.rolePermMenus.map(m => m.group === groupName ? { ...m, can_access: value } : m);
    },
    actionsForMenu(menuKey) {
      return this.rolePermActions.filter(a => a.menu_key === menuKey);
    },
    countAllowedActions(menuKey) {
      return this.rolePermActions.filter(a => a.menu_key === menuKey && a.allowed).length;
    },
    toggleMenuActions(menuKey, value) {
      this.rolePermActions = this.rolePermActions.map(a => a.menu_key === menuKey ? { ...a, allowed: value } : a);
    },
    onMenuToggle(m) {
      // When a menu is unticked, clear all its actions automatically
      if (!m.can_access) this.toggleMenuActions(m.key, false);
    },
    async savePermGroup() {
      if (!this.permGroupForm.name) { alert('Category name is required'); return; }
      this.savingRolePerms = true;
      this.roleMsg = '';
      try {
        const permissions = this.rolePermMenus.map(m => ({ menuKey: m.key, canAccess: !!m.can_access }));
        const actions = this.rolePermActions.map(a => ({ actionKey: a.key, allowed: !!a.allowed }));
        const params = {
          function: 'savePermissionGroup',
          name: this.permGroupForm.name,
          description: this.permGroupForm.description,
          defaultRole: this.permGroupForm.default_role || 'cashier',
          permissions,
          actions,
        };
        if (this.selectedGroupId) params.groupId = this.selectedGroupId;
        const r = await executeJson(params, simitrack);
        if (r.data.success) {
          this.roleMsg = r.data.message;
          const newId = (r.data.data && r.data.data.groupId) || this.selectedGroupId;
          await this.loadPermGroups();
          if (newId) await this.loadPermGroup(newId);
        } else { alert(r.data.message || 'Save failed'); }
      } catch (e) { alert('Save failed'); }
      this.savingRolePerms = false;
      setTimeout(() => { this.roleMsg = ''; }, 3000);
    },
    async deletePermGroup() {
      if (!this.selectedGroupId) return;
      if (!confirm('Delete this category? Users currently in this category will lose all menu access until reassigned.')) return;
      this.savingRolePerms = true;
      try {
        const r = await executeJson({ function: 'deletePermissionGroup', groupId: this.selectedGroupId }, simitrack);
        if (r.data.success) {
          this.roleMsg = 'Category deleted';
          this.cancelPermGroup();
          await this.loadPermGroups();
        } else { alert(r.data.message || 'Delete failed'); }
      } catch (e) { alert('Delete failed'); }
      this.savingRolePerms = false;
      setTimeout(() => { this.roleMsg = ''; }, 3000);
    },
    async loadSettings() {
      this.loading = true;
      try {
        var r = await executeJson({ function: 'getSettings' }, simitrack);
        if (r.data.success && r.data.data) {
          var d = r.data.data;
          if (d.profile) Object.assign(this.profile, d.profile);
          if (d.sms) Object.assign(this.sms, d.sms);
          if (d.email) Object.assign(this.email, d.email);
          if (d.paybills) this.paybills = d.paybills.map(p => ({...p, _testing: false, _registering: false, _stkTesting: false, _msg: '', _msgOk: false}));
          if (d.modules) Object.assign(this.modules, d.modules);
          if (d.attendance) Object.assign(this.attendance, d.attendance);
          if (d.fees) Object.assign(this.fees, d.fees);
          if (d.billing) Object.assign(this.billing, d.billing);
          if (d.smsBalance !== undefined) this.smsBalance = d.smsBalance;
        }
      } catch (e) { console.error(e); }
      this.loading = false;
    },
    async saveSection(section, data) {
      this.saving = true; this.saveMsg = '';
      try {
        var r = await executeJson({ function: 'updateSettings', section: section, settings: JSON.stringify(data) }, simitrack);
        if (r.data.success) { this.saveMsg = section; setTimeout(() => { this.saveMsg = ''; }, 3000); }
        else alert(r.data.message || 'Failed');
      } catch (e) { alert('Connection error'); }
      this.saving = false;
    },
    saveProfile() {
      this.saveSection('profile', this.profile);
      // Cache for branding across pages
      sessionStorage.setItem('institutionName', this.profile.name || '');
      sessionStorage.setItem('institutionLogo', this.profile.logo || '');
      sessionStorage.setItem('institutionMotto', this.profile.motto || '');
      sessionStorage.setItem('institutionAddress', this.profile.address || '');
      sessionStorage.setItem('institutionPhone', this.profile.phone || '');
      sessionStorage.setItem('institutionEmail', this.profile.email || '');
    },
    async uploadLogo(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) { alert('Please select an image file'); return; }
      if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
      this.logoUploading = true;
      try {
        const formData = new FormData();
        formData.append('function', 'uploadInstitutionLogo');
        formData.append('logo', file);
        const token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
        const res = await fetch('https://api.paysol.africa/simitrack/', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + token },
          body: formData
        });
        const data = await res.json();
        if (data.success) {
          this.profile.logo = data.data?.url || '';
          sessionStorage.setItem('institutionLogo', this.profile.logo);
          this.saveProfile();
        } else { alert(data.message || 'Upload failed'); }
      } catch(e) { alert('Upload error'); }
      this.logoUploading = false;
    },
    removeLogo() {
      this.profile.logo = '';
      sessionStorage.removeItem('institutionLogo');
      this.saveProfile();
    },
    async uploadFile(e, profileKey, serverKey) {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) { alert('Please select an image file'); return; }
      if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
      this.fileUploading = true;
      try {
        const formData = new FormData();
        formData.append('function', 'uploadInstitutionFile');
        formData.append('file', file);
        formData.append('fileKey', serverKey);
        const token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
        const res = await fetch('https://api.paysol.africa/simitrack/', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + token },
          body: formData
        });
        const data = await res.json();
        if (data.success) {
          this.profile[profileKey] = data.data?.url || '';
          sessionStorage.setItem(profileKey, this.profile[profileKey]);
          this.saveProfile();
        } else { alert(data.message || 'Upload failed'); }
      } catch(e) { alert('Upload error'); }
      this.fileUploading = false;
    },
    removeFile(profileKey) {
      this.profile[profileKey] = '';
      sessionStorage.removeItem(profileKey);
      this.saveProfile();
    },
    saveSms() { this.saveSection('sms', this.sms); },
    saveEmail() { this.saveSection('email', this.email); },
    // === PAYBILL MANAGEMENT ===
    resetPbForm() {
      this.pbForm = { shortcode: '', tillNumber: '', accountPrefix: '', label: 'M-Pesa', purpose: 'all', type: 'paybill', consumerKey: '', consumerSecret: '', passkey: '', environment: 'production', isPrimary: false, initiatorName: '', securityCredential: '' };
    },
    async savePaybill() {
      if (!this.pbForm.shortcode || !this.pbForm.consumerKey || !this.pbForm.consumerSecret) {
        alert('Shortcode, Consumer Key, and Consumer Secret are required.'); return;
      }
      this.saving = true;
      try {
        if (this.editingPaybill) {
          await executeJson({ function: 'updatePaybill', paybillId: this.editingPaybill.id, ...this.pbForm }, simitrack);
        } else {
          await executeJson({ function: 'addPaybill', ...this.pbForm }, simitrack);
        }
        this.editingPaybill = null; this.showAddPaybill = false;
        this.resetPbForm();
        await this.loadSettings();
      } catch (e) { alert('Failed to save paybill'); }
      this.saving = false;
    },
    editPaybill(pb) {
      this.editingPaybill = pb;
      this.showAddPaybill = false;
      this.pbForm = {
        shortcode: pb.shortcode, tillNumber: pb.tillNumber || '', accountPrefix: pb.accountPrefix || '', label: pb.label, purpose: pb.purpose, type: pb.type,
        consumerKey: pb.consumerKey, consumerSecret: pb.consumerSecret, passkey: pb.passkey,
        environment: pb.environment, isPrimary: pb.isPrimary,
        initiatorName: pb.initiatorName || '', securityCredential: pb.securityCredential || '',
      };
    },
    async deletePaybill(pb) {
      if (!confirm(`Delete paybill ${pb.shortcode} (${pb.label})?`)) return;
      try {
        const res = await executeJson({ function: 'deletePaybill', paybillId: pb.id }, simitrack);
        if (!res.data.success) { alert(res.data.message); return; }
        await this.loadSettings();
      } catch (e) { alert('Failed to delete'); }
    },
    async setPrimaryPaybill(pb) {
      try {
        await executeJson({ function: 'updatePaybill', paybillId: pb.id, isPrimary: 1 }, simitrack);
        await this.loadSettings();
      } catch (e) { alert('Failed'); }
    },
    async testPaybillConnection(pb) {
      pb._testing = true; pb._msg = ''; pb._msgOk = false;
      try {
        const res = await executeJson({ function: 'mpesaTestConnection', paybillId: pb.id }, simitrack);
        pb._msg = res.data.message; pb._msgOk = res.data.success;
      } catch (e) { pb._msg = 'Connection error'; pb._msgOk = false; }
      pb._testing = false;
    },
    async registerPaybillCallbacks(pb) {
      pb._registering = true; pb._msg = ''; pb._msgOk = false;
      try {
        const res = await executeJson({ function: 'registerMpesaCallbacks', paybillId: pb.id }, simitrack);
        pb._msg = res.data.message; pb._msgOk = res.data.success;
        if (res.data.success) pb.callbacksRegistered = true;
      } catch (e) { pb._msg = 'Connection error'; pb._msgOk = false; }
      pb._registering = false;
    },
    async testStkForPaybill(pb) {
      const phone = prompt('Enter phone number for STK test:', sessionStorage.getItem('userPhone') || '');
      if (!phone) return;
      const amount = parseInt(prompt('Amount (KSh):', '1')) || 1;
      pb._stkTesting = true; pb._msg = ''; pb._msgOk = false;
      try {
        const res = await executeJson({ function: 'testMpesaStk', paybillId: pb.id, phone, amount }, simitrack);
        pb._msg = res.data.message; pb._msgOk = res.data.success;
        if (res.data.success) {
          // STK sent successfully — mark as activated
          pb.callbacksRegistered = true;
          try { await executeJson({ function: 'updatePaybill', paybillId: pb.id, isActive: 1 }, simitrack); } catch(e) {}
        }
      } catch (e) { pb._msg = 'Connection error'; pb._msgOk = false; }
      pb._stkTesting = false;
    },
    saveModules() { this.saveSection('modules', this.modules); sessionStorage.setItem('enabledModules', JSON.stringify(this.modules)); },
    async loadSmsBundles() {
      try {
        const res = await executeJson({ function: 'getSMSBundles' }, simitrack);
        if (res.data.success) this.smsBundles = res.data.data || [];
      } catch (e) { console.error('Failed to load bundles', e); }
      this.smsTopUpPhone = sessionStorage.getItem('userPhone') || '';
      this.testSmsPhone = sessionStorage.getItem('userPhone') || '';
    },
    async buySmsBundle() {
      if (!this.selectedBundle) return;
      if (!this.smsTopUpPhone || this.smsTopUpPhone.length < 9) { this.smsBuyResult = 'Enter valid phone'; this.smsBuyOk = false; return; }
      this.smsBuying = true; this.smsBuyResult = '';
      try {
        const res = await executeJson({ function: 'buySMSBundle', bundleId: this.selectedBundle.id, phone: this.smsTopUpPhone }, simitrack);
        this.smsBuyResult = res.data.message; this.smsBuyOk = res.data.success;
        if (res.data.success) {
          // Poll for completion
          setTimeout(() => { this.loadSettings(); }, 15000); // Refresh balance after 15s
        }
      } catch (e) { this.smsBuyResult = 'Connection error'; this.smsBuyOk = false; }
      this.smsBuying = false;
    },
    async sendTestSms() {
      if (!this.testSmsPhone || this.testSmsPhone.length < 9) { this.testSmsResult = 'Enter valid phone'; this.testSmsOk = false; return; }
      this.testSmsSending = true; this.testSmsResult = '';
      try {
        const res = await executeJson({ function: 'sendTestSms', phone: this.testSmsPhone, message: this.testSmsMessage }, simitrack);
        this.testSmsResult = res.data.message; this.testSmsOk = res.data.success;
        if (res.data.success && res.data.data?.balance !== undefined) this.smsBalance = res.data.data.balance;
      } catch (e) { this.testSmsResult = 'Connection error'; this.testSmsOk = false; }
      this.testSmsSending = false;
    },
    saveAttendance() { this.saveSection('attendance', this.attendance); },
    saveFees() { this.saveSection('fees', this.fees); },
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-settings-layout{display:flex;gap:24px;min-height:70vh;}
.st-settings-nav{width:240px;flex-shrink:0;background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:12px;height:fit-content;position:sticky;top:90px;}
.st-nav-item{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:10px;color:#8BA4C4;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;margin-bottom:2px;}
.st-nav-item:hover{background:rgba(46,134,222,.08);color:#E8EDF4;}
.st-nav-item.active{background:rgba(46,134,222,.15);color:#4FC3F7;border:1px solid rgba(46,134,222,.3);}
.st-nav-item i{font-size:18px;width:22px;text-align:center;}
.st-settings-content{flex:1;min-width:0;}
.st-section{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:28px;}
.st-section-title{color:#E8EDF4;font-size:18px;font-weight:700;margin-bottom:6px;}
.st-section-desc{color:#8BA4C4;font-size:13px;margin-bottom:20px;}
.st-label{color:#8BA4C4;font-size:13px;font-weight:600;margin-bottom:6px;display:block;}
.st-input,.st-select{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:10px 14px;color:#E8EDF4;font-size:14px;outline:none;transition:border .2s;}
.st-input:focus,.st-select:focus{border-color:#2E86DE;}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:10px;padding:12px 28px;font-size:15px;}
.st-btn-primary:disabled{opacity:.6;}
.st-btn-success{background:#10B981;color:#fff;border:none;font-weight:600;border-radius:10px;padding:12px 28px;font-size:15px;}
.st-btn-danger{background:#EF4444;color:#fff;border:none;font-weight:600;border-radius:10px;padding:12px 28px;font-size:15px;}
.st-btn-outline{background:transparent;border:1px solid #4FC3F7;color:#4FC3F7;font-weight:600;border-radius:10px;padding:12px 28px;font-size:15px;}
.st-btn-outline:hover{background:rgba(79,195,247,.1);}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;margin-bottom:16px;}
.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#6EE7B7;}
.st-info-box{background:#0A1628;border:1px solid #1A3A6A;border-radius:12px;padding:16px;}
.st-url-row{display:flex;align-items:center;gap:8px;padding:4px 0;font-size:12px;}
.st-url-label{color:#8BA4C4;min-width:120px;flex-shrink:0;}
.st-url-val{color:#4FC3F7;font-family:monospace;font-size:11px;word-break:break-all;}
.st-spinner{display:flex;justify-content:center;padding:40px;}.st-spinner .spinner-border{color:#2E86DE;}
.st-table{width:100%;border-collapse:collapse;}
.st-table th{color:#8BA4C4;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:1px;padding:10px 12px;border-bottom:1px solid #1A3A6A;text-align:left;}
.st-table td{color:#E8EDF4;font-size:13px;padding:12px;border-bottom:1px solid #1A3A6A;}
.st-badge{padding:3px 10px;border-radius:50px;font-size:11px;font-weight:600;}
.st-badge-success{background:rgba(16,185,129,.15);color:#10B981;}
.st-badge-warning{background:rgba(245,158,11,.15);color:#F59E0B;}
.st-empty-sm{color:#8BA4C4;font-size:13px;padding:20px 0;text-align:center;}

/* Module Cards */
.st-module-grid{display:flex;flex-direction:column;gap:8px;}
.st-module-card{background:#0A1628;border:1px solid #1A3A6A;border-radius:12px;padding:14px 16px;}
.st-module-icon{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0;}
.st-module-name{color:#E8EDF4;font-size:14px;font-weight:700;}
.st-module-desc{color:#8BA4C4;font-size:11px;margin-top:1px;}

/* Toggle Switch */
.st-toggle{position:relative;width:48px;height:26px;flex-shrink:0;}
.st-toggle input{opacity:0;width:0;height:0;}
.st-toggle-slider{position:absolute;inset:0;background:#1A3A6A;border-radius:26px;cursor:pointer;transition:.3s;}
.st-toggle-slider:before{content:'';position:absolute;width:20px;height:20px;left:3px;bottom:3px;background:#8BA4C4;border-radius:50%;transition:.3s;}
.st-toggle input:checked + .st-toggle-slider{background:#2E86DE;}
.st-toggle input:checked + .st-toggle-slider:before{transform:translateX(22px);background:#fff;}

/* Plan Card */
.st-plan-card{background:linear-gradient(135deg,#0D2B5E,#1A3A6A);border:1px solid #2E86DE;border-radius:14px;padding:24px;margin-bottom:20px;}
.st-plan-name{color:#E8EDF4;font-size:20px;font-weight:800;}
.st-plan-status{color:#8BA4C4;font-size:13px;margin-top:4px;}
.text-success{color:#10B981!important;}
.text-warning{color:#F59E0B!important;}

/* Usage Grid */
.st-usage-grid{display:grid;grid-template-columns:repeat(auto-fill, minmax(180px, 1fr));gap:12px;}
.st-usage-item{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:14px;}
.st-usage-label{color:#8BA4C4;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;}
.st-usage-val{color:#E8EDF4;font-size:22px;font-weight:800;margin-top:4px;}.st-usage-val span{color:#8BA4C4;font-size:14px;font-weight:400;}

@media(max-width:768px){
  .st-settings-layout{flex-direction:column;}
  .st-settings-nav{width:100%;position:static;display:flex;flex-wrap:wrap;gap:4px;padding:8px;}
  .st-nav-item{padding:7px 12px;font-size:12px;}
  .st-nav-item i{display:none;}
}
.logo-preview{width:80px;height:80px;border-radius:12px;border:2px dashed #1A3A6A;cursor:pointer;overflow:hidden;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:.2s;background:#0A1628;}
.logo-preview:hover{border-color:#4FC3F7;}
.logo-img{width:100%;height:100%;object-fit:contain;}
.logo-placeholder{display:flex;flex-direction:column;align-items:center;color:#4A6A8A;}
.sig-preview{width:120px;height:60px;border-radius:8px;border:2px dashed #1A3A6A;cursor:pointer;overflow:hidden;display:flex;align-items:center;justify-content:center;background:#0A1628;transition:.2s;}.sig-preview:hover{border-color:#4FC3F7;}
.sig-img{width:100%;height:100%;object-fit:contain;padding:4px;}
.sig-placeholder{display:flex;flex-direction:column;align-items:center;color:#4A6A8A;font-size:10px;gap:2px;}
.st-grading-card{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px 14px;cursor:pointer;transition:.15s;}
.st-grading-card:hover{border-color:#4FC3F7;}
.st-grading-card.active{border-color:#4FC3F7;background:rgba(46,134,222,.1);}
.st-preset-row{display:flex;align-items:center;justify-content:space-between;gap:12px;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px 14px;margin-bottom:8px;cursor:pointer;transition:.15s;}
.st-preset-row:hover{border-color:#4FC3F7;}
.st-preset-row.suggested{border-color:rgba(16,185,129,.4);background:rgba(16,185,129,.04);}
</style>